import { sendAnalytics } from '@audio-network/analytics'
import { Divider, Tabs, Tray } from '@audio-network/componentlibrary'
import { useTranslation } from 'next-i18next'
import { useEffect } from 'react'

import TrackTrayHeader from '@/components/shared/TrackActions/TrackTray/TrackTrayHeader/TrackTrayHeader'
import TrackMixes from '@/components/shared/TrackMixes/TrackMixes'
import TrackRelatedTracks from '@/components/shared/TrackRelatedTracks/TrackRelatedTracks'
import useNestedTracksStore, {
	NestedTracksTab,
} from '@/stores/nestedTracksStore'

import { NESTED_TRACKS_TRAY_ID } from './NestedTracks.constants'
import {
	useClearNestedTracksStoreOnDialogClose,
	useCloseNestedTracksTrayOnRouteChange,
} from './NestedTracks.hooks'
import styles from './NestedTracks.module.css'
import { scrollNestedTracksTrayToTop } from './NestedTracks.utils'
import NestedTracksTrayControls from './NestedTracksTrayControls'

type TrayTab = {
	/** Matches the track page `Track Details Tab` event names. */
	analyticsName: string
	nestedTracksTab: NestedTracksTab
	title: string
	content: JSX.Element
	initialTab: boolean
}

/**
 * This component will be used to display mixes and stems for a track as well as similar tracks.
 *
 * NestedTracks might not be the best name long term.
 */
const NestedTracks = (): JSX.Element => {
	const { t } = useTranslation(['track'])
	const track = useNestedTracksStore((state) => state.track)
	const source = useNestedTracksStore((state) => state.source)
	const tab = useNestedTracksStore((state) => state.tab)
	const navigationId = useNestedTracksStore((state) => state.navigationId)
	const changeTab = useNestedTracksStore((state) => state.changeTab)

	useClearNestedTracksStoreOnDialogClose()
	useCloseNestedTracksTrayOnRouteChange()

	useEffect(() => {
		const dialog = document.getElementById(NESTED_TRACKS_TRAY_ID)
		const scrollContainer = dialog?.firstElementChild

		if (scrollContainer instanceof HTMLElement) {
			scrollContainer.dataset.nestedTracksTrayScroll = ''
		}
	}, [])

	const tabs: TrayTab[] = [
		{
			analyticsName: 'Mixes',
			nestedTracksTab: 'mixes',
			title: t('mixes'),
			content: <TrackMixes skipQueueInitialisation track={track} />,
			initialTab: tab === 'mixes',
		},
		{
			analyticsName: 'Similar Tracks',
			nestedTracksTab: 'similar-tracks',
			title: t('similar-tracks'),
			content: (
				<TrackRelatedTracks
					data-testid="nested-tracks-similar-list"
					onHandpickedFetchBegin={scrollNestedTracksTrayToTop}
					track={track}
				/>
			),
			initialTab: tab === 'similar-tracks',
		},
	]

	const handleTabChange = (title: string): void => {
		const selectedTab = tabs.find((tabConfig) => tabConfig.title === title)

		if (!selectedTab) {
			return
		}

		sendAnalytics({
			noun: 'Track Details Tab',
			pastTenseVerb: 'Clicked',
			eventParameters: { Name: selectedTab.analyticsName },
		})
		changeTab(selectedTab.nestedTracksTab)
	}

	return (
		<Tray
			className={styles.nestedTracksTray}
			closedby="closerequest"
			id={NESTED_TRACKS_TRAY_ID}
		>
			<NestedTracksTrayControls />
			{track ? (
				<>
					<TrackTrayHeader track={track} source={source ?? 'Similar Tracks'} />
					<Divider />
					{/* Remount when the tray navigates so `initialTab` reflects the current entry. */}
					<Tabs key={navigationId} onTabChange={handleTabChange} tabs={tabs} />
				</>
			) : null}
		</Tray>
	)
}

export default NestedTracks
